<script lang="ts">
	import type { i18n } from 'types/common'

	import { getEventChecked } from '@isoftdata/browser-event'
	import { graphql, type AddressSearchStatusCodeEnum$options as AddressValidationStatusCode } from '$houdini'
	import AddressFields, { type AddressData, type State } from './AddressFields.svelte'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Icon from '@isoftdata/svelte-icon'
	import Modal from '@isoftdata/svelte-modal'
	import Table, { type Column, Td } from '@isoftdata/svelte-table'
	import { getContext } from 'svelte'

	type ValidationResult = {
		address: string | null
		city: string | null
		state: string | null
		zip: string | null
		country: string | null
	}

	interface Props {
		states?: Array<State>
		selectedAddressData: (addressData: AddressData) => void
	}

	let { states = [], selectedAddressData }: Props = $props()

	let show = $state(false)
	let addressData: AddressData = $state({
		companyName: '',
		contactName: '',
		street: '',
		city: '',
		state: null,
		zip: null,
		country: null,
		email: '',
		faxNumber: '',
		phoneNumber: '',
		mobilePhoneNumber: '',
		validated: false,
	})
	let validationResults: Array<ValidationResult> = $state([])
	let validationError: string | null = $state(null)
	let validationMessage: AddressValidationStatusCode | null = $state(null)
	let isValidating = $state(false)
	let addressChanged = $state(false)
	let selectedAddressMatch: ValidationResult | null = $state(null)

	const { t: translate } = getContext<i18n>('i18next')

	const validationStatusMessageMap: Record<AddressValidationStatusCode, { message: string; alertColor: string }> = {
		None: { message: translate('addressValidation.noMatchesFound', 'No matches found'), alertColor: 'danger' },
		Partial: {
			message: translate('addressValidation.partialMatchesFound', 'Partial matches found'),
			alertColor: 'primary',
		},
		Verified: { message: translate('addressValidation.addressVerified', 'Address verified'), alertColor: 'success' },
	}
	const columns: Array<Column> = [
		{ property: 'selected', name: translate('common:use', 'Use'), align: 'center', width: '1rem' },
		{ property: 'address', name: translate('common:address', 'Address') },
		{ property: 'city', name: translate('common:city', 'City') },
		{ property: 'state', name: translate('common:state', 'State') },
		{ property: 'zip', name: translate('common:zip', 'Zip') },
		{ property: 'country', name: translate('common:country', 'Country') },
	]

	const addressSearchQuery = graphql(`
		query AddressSearch($input: AddressSearchInput!) {
			addressSearch(input: $input) {
				message
				status
				matches {
					address1
					city
					country
					plus4
					state
					zip
				}
			}
		}
	`)

	export async function open(addressToValidate: AddressData) {
		addressData = addressToValidate
		show = true
		await validateAddress()
	}

	function close() {
		show = false
		addressData = {
			...addressData,
			street: '',
			city: '',
			state: '',
			zip: '',
			country: '',
		}
		selectedAddressMatch = null
		validationResults = []
		validationError = null
		validationMessage = null
		isValidating = false
		addressChanged = false
	}

	async function validateAddress() {
		validationError = null
		isValidating = true
		try {
			const { data } = await addressSearchQuery.fetch({
				variables: {
					input: {
						address1: addressData.street,
						city: addressData.city,
						state: addressData.state,
						zip: addressData.zip,
						country: addressData.country,
					},
				},
			})
			if (data?.addressSearch) {
				validationMessage = data.addressSearch.status
				validationResults = data.addressSearch.matches.reduce((acc: Array<ValidationResult>, match) => {
					if (!match) {
						return acc
					}
					acc.push({
						address: match.address1,
						city: match.city,
						state: match.state,
						zip: match.zip,
						country: match.country,
					})
					return acc
				}, [])
			}
		} catch (error: any) {
			validationError = error.message
		}
		isValidating = false
		addressChanged = false
	}
</script>

<Modal
	backdropClickCancels={false}
	{close}
	modalSize="xl"
	title={translate('addressValidation.validateAddress', 'Validate Address')}
	confirmButtonDisabled={isValidating || !selectedAddressMatch || selectedAddressMatch === null}
	bind:show
	confirm={() => {
		selectedAddressData(addressData)
		show = false
	}}
>
	<div class="row">
		<div class="col-12 col-md-5">
			<div class="card">
				<div class="card-header">
					<h5 class="mb-0">{translate('common:address', 'Address')}</h5>
				</div>
				<div class="card-body">
					<form
						id="address-form"
						onsubmit={event => {
							event.preventDefault()
							validateAddress()
						}}
					>
						<AddressFields
							disableAllFields={false}
							{states}
							addressType="VALIDATION"
							bind:addressData
							addressChange={() => (addressChanged = true)}
						/>
					</form>
				</div>
				<div class="card-footer d-flex justify-content-between">
					<Button
						outline
						type="submit"
						form="address-form"
						size="sm"
						color="primary"
						iconClass="magnifying-glass"
						disabled={!addressChanged}
						isLoading={isValidating}
					>
						{translate('salesOrder.validate', 'Validate')}
					</Button>
					<Button
						outline
						size="sm"
						color="danger"
						iconClass="broom"
						onclick={() => {
							addressData = {
								...addressData,
								street: '',
								city: '',
								state: '',
								zip: '',
								country: '',
							}
							selectedAddressMatch = null
						}}
					>
						{translate('common:clear', 'Clear')}
					</Button>
				</div>
			</div>
		</div>
		<div class="col-12 col-md-7">
			<div class="card h-100">
				<div class="card-header">
					<h5 class="mb-0">{translate('addressValidation.matches', 'Matches')}</h5>
				</div>
				<div class="card-body">
					{#if isValidating}
						<div class="text-center">
							<Icon
								icon="spinner"
								class="fa-spin"
								size="xl"
							/>
						</div>
					{:else if validationError}
						<div
							class="alert alert-danger"
							role="alert"
						>
							{validationError}
						</div>
					{:else}
						<Table
							responsive
							{columns}
							rows={validationResults}
						>
							{#snippet noRows({ visibleColumnsCount })}
								<tr>
									<td
										class="text-center"
										colspan={visibleColumnsCount}
										>{translate('addressValidation.noResultsFound', 'No results found')}</td
									>
								</tr>
							{/snippet}
							{#snippet children({ row })}
								<tr class:table-primary={row === selectedAddressMatch}>
									<Td property="selected">
										<Checkbox
											showLabel={false}
											checked={row === selectedAddressMatch}
											onchange={event => {
												const value = getEventChecked(event)
												if (value) {
													selectedAddressMatch = row
													addressData = {
														...addressData,
														street: row.address,
														city: row.city,
														state: row.state,
														zip: row.zip,
														country: row.country,
														validated: true,
													}
												}
											}}
										/>
									</Td>
									<Td property="address">{row.address}</Td>
									<Td property="city">{row.city}</Td>
									<Td property="state">{row.state}</Td>
									<Td property="zip">{row.zip}</Td>
									<Td property="country">{row.country}</Td>
								</tr>
							{/snippet}
						</Table>
						{#if validationMessage && validationResults.length > 0}
							<div
								class="alert alert-{validationStatusMessageMap[validationMessage].alertColor} mt-2"
								role="alert"
							>
								{validationStatusMessageMap[validationMessage].message}
							</div>
						{/if}
					{/if}
				</div>
			</div>
		</div>
	</div>
</Modal>
